import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, errMsg, inr, px, sign } from "@/lib/api";

const ist = () => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false })
    .formatToParts(new Date()).map((x) => [x.type, x.value]));
  return { wd: p.weekday, m: Number(p.hour) * 60 + Number(p.minute) };
};
const useIst = () => {
  const [t, setT] = useState(ist());
  useEffect(() => { const i = setInterval(() => setT(ist()), 20000); return () => clearInterval(i); }, []);
  return t;
};
const weekday = (wd) => !["Sat", "Sun"].includes(wd);

const DOT = { ok: "bg-emerald-400", warn: "bg-amber-400", bad: "bg-red-500", off: "bg-slate-500" };
const TXT = { ok: "text-emerald-300", warn: "text-amber-300", bad: "text-red-300", off: "text-slate-400" };

// Top bar: engine state at a glance; tap opens the dashboard
export function EngineBadge() {
  const [e, setE] = useState(null);
  useEffect(() => {
    const load = () => api.get("/engine").then((r) => setE(r.data)).catch(() => {});
    load();
    const i = setInterval(load, 10000);
    return () => clearInterval(i);
  }, []);
  if (!e) return null;
  return (
    <Link to="/" title={e.on ? e.status : "Engine OFF"} data-testid="engine-badge" className="flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-[#1E293B] hover:border-[#334155]">
      <span className={`w-2 h-2 rounded-full ${DOT[e.level]} ${e.level === "ok" && e.word === "Running" ? "animate-pulse" : ""}`} />
      <span className={TXT[e.level]} data-testid="engine-badge-word">{e.word}</span>
    </Link>
  );
}

// From 08:45 on market days (until 15:30): big reminder when Zerodha is not logged in
export function LoginBanner({ kite }) {
  const t = useIst();
  const [busy, setBusy] = useState(false);
  if (!kite?.configured || kite.logged_in || !weekday(t.wd) || t.m < 8 * 60 + 45 || t.m >= 15 * 60 + 30) return null;
  const login = async () => {
    setBusy(true);
    try { window.location.href = (await api.get("/kite/login_url")).data.url; } catch (x) { toast.error(errMsg(x)); setBusy(false); }
  };
  const late = t.m >= 9 * 60 + 10;
  return (
    <div className="max-w-[1500px] mx-auto px-4 pt-3" data-testid="login-banner">
      <div className="panel p-4 border-red-500/60 bg-red-500/10 flex flex-wrap items-center gap-3">
        <AlertTriangle className="text-red-300 shrink-0" size={22} />
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-semibold text-red-300">Zerodha is not logged in{late ? " — the engine can't start your run" : ""}</p>
          <p className="text-xs text-slate-300">{late ? "Log in now; the engine starts the run within seconds." : "Log in before 09:10 so the engine starts your run on time."}</p>
        </div>
        <button onClick={login} disabled={busy} data-testid="login-banner-btn" className="h-11 px-6 rounded-md bg-red-500 hover:bg-red-400 text-white font-semibold disabled:opacity-60">
          {busy ? "Opening Zerodha…" : "Log in to Zerodha"}
        </button>
      </div>
    </div>
  );
}

// From 15:30 on market days: today's trades and net P&L
export function DailySummary() {
  const t = useIst();
  const [d, setD] = useState(null);
  const show = weekday(t.wd) && t.m >= 15 * 60 + 30;
  useEffect(() => { if (show) api.get("/pnl/today").then((r) => setD(r.data)).catch(() => {}); }, [show]);
  if (!show || !d) return null;
  return (
    <div className="panel p-4 space-y-3" data-testid="daily-summary">
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="label-xs">Today · {d.date}{d.mode ? ` · ${d.mode.toUpperCase()}` : ""}</span>
        <span className={`num text-2xl font-semibold ${sign(d.net)}`} data-testid="daily-summary-net">{inr(d.net)}</span>
        <span className="text-xs text-slate-400">{d.trades.length} trade{d.trades.length === 1 ? "" : "s"} · {d.wins} won · {d.losses} lost{d.charges ? ` · charges ${inr(d.charges)}` : ""}</span>
      </div>
      {d.trades.length ? (
        <div className="space-y-1" data-testid="daily-summary-trades">
          {d.trades.map((x, i) => (
            <div key={i} className="flex flex-wrap gap-x-3 text-xs num">
              <span className="font-semibold text-slate-100 w-24">{x.sym}</span><span className={x.side === "LONG" ? "up" : "down"}>{x.side}</span>
              <span className="text-slate-400">{x.t_in} @ {px(x.entry)} → {x.t_out} @ {px(x.exit)}</span><span className={`ml-auto ${sign(x.net)}`}>{inr(x.net)}</span>
            </div>
          ))}
        </div>
      ) : <p className="text-xs text-slate-500">No trades today.</p>}
    </div>
  );
}
